/* noew: click distortion for pill buttons - a gooey green glow that sweeps over
   the button from bottom-left to top-right (live version of button-distortion v6).

   Blurred copies of the button shape are warped inside a moving diagonal band and
   coloured with a black -> green -> white ramp, into a transparent canvas that sits
   over the button. Sizes are relative to the button height, so it fits any button.

   Usage: const fx = window.noewGlowDistortion(buttonEl, { mode: 'glow' | 'full' });
          fx.play().then(...)   // resolves when the sweep is done
   'glow' = only the green halo; 'full' = also the dark swell and blurred label. */
(() => {
  const DURATION = 2.8; // s
  const ANGLE = (58 * Math.PI) / 180;
  const DX = Math.cos(ANGLE);
  const DY = -Math.sin(ANGLE);
  const LEVELS = [0, 0.25, 0.5, 0.75, 1];

  // colour ramp sampled from the reference (field 0 = outside, 1 = inside)
  const STOPS = [
    [0.0, [255, 255, 255]],
    [0.04, [232, 251, 226]],
    [0.1, [205, 250, 180]],
    [0.17, [170, 250, 120]],
    [0.25, [150, 248, 140]],
    [0.33, [118, 228, 160]],
    [0.42, [70, 150, 110]],
    [0.53, [34, 78, 56]],
    [0.66, [22, 50, 37]],
    [0.82, [9, 22, 15]],
    [0.93, [2, 6, 4]],
    [1.0, [0, 0, 0]],
  ];
  const LUT = new Uint8ClampedArray(256 * 3);
  for (let i = 0; i < 256; i++) {
    const f = i / 255;
    let j = 1;
    while (j < STOPS.length - 1 && STOPS[j][0] < f) j++;
    const [f0, c0] = STOPS[j - 1];
    const [f1, c1] = STOPS[j];
    const t = Math.min(1, Math.max(0, (f - f0) / (f1 - f0)));
    for (let c = 0; c < 3; c++) LUT[i * 3 + c] = c0[c] + (c1[c] - c0[c]) * t;
  }

  const smooth = (t) => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));

  // CSS-style cubic-bezier(0.7, 0, 0.3, 1): strong ease-in and ease-out
  const ease = (t) => {
    let lo = 0;
    let hi = 1;
    let s = 0.5;
    for (let i = 0; i < 24; i++) {
      s = (lo + hi) / 2;
      const x = 3 * (1 - s) * (1 - s) * s * 0.7 + 3 * (1 - s) * s * s * 0.3 + s * s * s;
      if (x < t) lo = s;
      else hi = s;
    }
    return 3 * (1 - s) * s * s + s * s * s;
  };

  const blur = (src, w, h, sigma) => {
    const r = Math.ceil(sigma * 3);
    const k = new Float32Array(r * 2 + 1);
    let sum = 0;
    for (let i = -r; i <= r; i++) sum += k[i + r] = Math.exp(-(i * i) / (2 * sigma * sigma));
    for (let i = 0; i < k.length; i++) k[i] /= sum;
    const tmp = new Float32Array(w * h);
    const out = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
      const row = y * w;
      for (let x = 0; x < w; x++) {
        let v = 0;
        for (let i = -r; i <= r; i++) v += src[row + Math.min(w - 1, Math.max(0, x + i))] * k[i + r];
        tmp[row + x] = v;
      }
    }
    for (let x = 0; x < w; x++) {
      for (let y = 0; y < h; y++) {
        let v = 0;
        for (let i = -r; i <= r; i++) v += tmp[Math.min(h - 1, Math.max(0, y + i)) * w + x] * k[i + r];
        out[y * w + x] = v;
      }
    }
    return out;
  };

  const sample = (f, w, h, x, y) => {
    x = Math.min(w - 1.001, Math.max(0, x));
    y = Math.min(h - 1.001, Math.max(0, y));
    const x0 = x | 0;
    const y0 = y | 0;
    const fx = x - x0;
    const fy = y - y0;
    const i = y0 * w + x0;
    const a = f[i] + (f[i + 1] - f[i]) * fx;
    const b = f[i + w] + (f[i + w + 1] - f[i + w]) * fx;
    return a + (b - a) * fy;
  };

  window.noewGlowDistortion = (btn, opts = {}) => {
    const mode = opts.mode === 'full' ? 'full' : 'glow';
    const glow = mode === 'glow';
    const host = btn.parentElement;
    const canvas = document.createElement('canvas');
    canvas.className = 'noew-glow-fx';
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'position:absolute;pointer-events:none;z-index:1;max-width:none;max-height:none;margin:0;';
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    let geo = null;
    let running = null;

    // shape + pre-blurred fields, rebuilt when the button size changes
    const build = () => {
      const r = Math.min(window.devicePixelRatio || 1, 1.5);
      const bw = Math.round(btn.offsetWidth * r);
      const bh = Math.round(btn.offsetHeight * r);
      if (geo && geo.bw === bw && geo.bh === bh) return geo;
      const s = bh / 256; // 1 unit = 1px of the 4x render the reference was tuned on
      const pad = Math.ceil(232 * s);
      const W = bw + pad * 2;
      const H = bh + pad * 2;
      const m = document.createElement('canvas');
      m.width = W;
      m.height = H;
      const mc = m.getContext('2d');
      mc.fillStyle = '#000';
      mc.beginPath();
      mc.roundRect(pad, pad, bw, bh, bh / 2);
      mc.fill();
      const label = btn.querySelector('.noew-btn__label');
      if (!glow && label) {
        const cs = getComputedStyle(label);
        mc.globalCompositeOperation = 'destination-out';
        mc.font = `${cs.fontWeight} ${parseFloat(cs.fontSize) * r}px ${cs.fontFamily}`;
        if ('letterSpacing' in mc) mc.letterSpacing = `${(parseFloat(cs.letterSpacing) || 0) * r}px`;
        mc.textAlign = 'center';
        mc.textBaseline = 'middle';
        mc.fillText(cs.textTransform === 'uppercase' ? label.textContent.trim().toUpperCase() : label.textContent.trim(), W / 2, H / 2);
      }
      const px = mc.getImageData(0, 0, W, H).data;
      const mask = new Float32Array(W * H);
      let u0 = Infinity;
      let u1 = -Infinity;
      for (let i = 0; i < W * H; i++) {
        mask[i] = px[i * 4 + 3] / 255;
        if (mask[i] > 0.5) {
          const u = (i % W) * DX + ((i / W) | 0) * DY;
          if (u < u0) u0 = u;
          if (u > u1) u1 = u;
        }
      }
      const fields = LEVELS.map((l) => (l === 0 ? mask : blur(mask, W, H, 64 * s * l)));
      canvas.width = W;
      canvas.height = H;
      canvas.style.width = `${W / r}px`;
      canvas.style.height = `${H / r}px`;
      canvas.style.left = `${btn.offsetLeft - pad / r}px`;
      canvas.style.top = `${btn.offsetTop - pad / r}px`;
      geo = { bw, bh, s, W, H, u0, u1, fields, img: ctx.createImageData(W, H), band: bw * 0.1 };
      return geo;
    };

    const frame = (g, t) => {
      const { W, H, s, fields, img, band } = g;
      const d = img.data;
      const p = t / DURATION;
      const e = ease(p);
      const cu = g.u0 - band * 2.2 + e * (g.u1 - g.u0 + band * 4.4);
      const env = Math.pow(Math.sin(Math.PI * e), 0.5);
      const ph = t * 4.2;
      const reach = band * 3.2;
      const inv2b2 = 1 / (2 * band * band);
      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          const o = (y * W + x) * 4;
          const du = x * DX + y * DY - cu;
          if (du > reach || du < -reach) {
            d[o + 3] = 0;
            continue;
          }
          const w = Math.exp(-du * du * inv2b2) * env;
          const k = smooth(w * 1.5);
          if (k < 0.004) {
            d[o + 3] = 0;
            continue;
          }
          // liquid wobble + slight trailing smear inside the band
          const sy = y + w * 12 * s * Math.sin(x / (90 * s) + ph) * Math.cos(y / (70 * s) - ph * 0.7) - w * 6 * s * DY;
          const sx = x + w * 16 * s * Math.sin(y / (70 * s) + ph * 1.3) - w * 14 * s * DX;
          const lv = glow ? Math.min(4, w * 6) : w * 4;
          const lo = Math.min(3, lv | 0);
          const fr = lv - lo;
          const a = sample(fields[lo], W, H, sx, sy);
          const b = sample(fields[lo + 1], W, H, sx, sy);
          const f = Math.min(1, (a + (b - a) * fr) * (1 + 0.12 * w));
          let alpha = smooth(f / 0.22) * k;
          let tone = f;
          if (glow) {
            // halo only: the button itself stays untouched; the ramp is stretched so
            // the edge (field ~0.5) starts at mint and fades out through lime
            alpha = smooth(f / 0.14) * k * (1 - fields[0][y * W + x]);
            tone = f * 0.8;
          }
          const c = Math.round(Math.max(tone, 0.1) * 255) * 3;
          d[o] = LUT[c];
          d[o + 1] = LUT[c + 1];
          d[o + 2] = LUT[c + 2];
          d[o + 3] = alpha * 255;
        }
      }
      ctx.putImageData(img, 0, 0);
    };

    const play = () => {
      if (running) return running;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !btn.offsetHeight) return Promise.resolve();
      const g = build();
      running = new Promise((resolve) => {
        const start = performance.now();
        const tick = (now) => {
          const t = (now - start) / 1000;
          if (t >= DURATION) {
            ctx.clearRect(0, 0, g.W, g.H);
            running = null;
            resolve();
            return;
          }
          frame(g, t);
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
      return running;
    };

    return { play };
  };
})();
